import {
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  OnChanges,
} from '@angular/core';
import { FormControl } from '@angular/forms';
import { Subject } from 'rxjs';
import { startWith, takeUntil } from 'rxjs/operators';
import { hashtagKey } from '../../utils/hashtag.utils';

/** Shape returned by `get-projects-filter-intital-data` under `hashtags`. */
export interface HashtagFilterOption {
  id?: string;
  name: string;
}

/**
 * Multi-select hashtag filter with an in-panel search box.
 *
 * Purely client-side: the complete tag list already arrives with the projects filter metadata,
 * so narrowing the list never hits the API. Selected values keep the `#` prefix that V1 persists.
 */
@Component({
  selector: 'app-hashtag-filter-select',
  templateUrl: './hashtag-filter-select.component.html',
  styleUrls: ['./hashtag-filter-select.component.scss'],
})
export class HashtagFilterSelectComponent implements OnInit, OnChanges, OnDestroy {
  @Input() options: HashtagFilterOption[] | null = [];
  @Input() selected: string[] = [];
  /** Flip this value to clear the selection from the parent. */
  @Input() reset = false;
  @Input() enableMatLabel = true;

  @Output() selectionChange = new EventEmitter<string[]>();

  searchControl = new FormControl('');
  /** All available tags, normalised and de-duplicated. */
  allHashtags: string[] = [];
  /** `allHashtags` narrowed by the search term. */
  filteredHashtags: string[] = [];
  selectedHashtags: string[] = [];

  private readonly _onDestroy = new Subject<void>();

  ngOnInit(): void {
    this.searchControl.valueChanges
      .pipe(startWith(this.searchControl.value ?? ''), takeUntil(this._onDestroy))
      .subscribe(() => this.applySearch());
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['options']) {
      // Keep the stored value. Legacy tags can contain spaces or mixed casing, and the filter API
      // must receive that exact value even though de-duplication and search use a normalised key.
      this.allHashtags = this.dedupePreservingStoredValue(
        (this.options || []).map((option) => option?.name),
      );
      this.applySearch();
    }

    if (changes['selected']) {
      this.selectedHashtags = this.dedupePreservingStoredValue(this.selected || []);
    }

    if (changes['reset'] && !changes['reset'].firstChange) {
      this.selectedHashtags = [];
      this.searchControl.setValue('', { emitEvent: true });
    }
  }

  ngOnDestroy(): void {
    this._onDestroy.next();
    this._onDestroy.complete();
  }

  get hasOptions(): boolean {
    return this.allHashtags.length > 0;
  }

  get allSelected(): boolean {
    return this.hasOptions && this.selectedHashtags.length === this.allHashtags.length;
  }

  get someSelected(): boolean {
    return this.selectedHashtags.length > 0 && !this.allSelected;
  }

  /** Tooltip listing the current selection, since the trigger only has room for a summary. */
  get selectionTooltip(): string {
    return this.selectedHashtags.join('\n');
  }

  onSelectionChange(value: string[]): void {
    this.selectedHashtags = value || [];
    this.selectionChange.emit([...this.selectedHashtags]);
  }

  /** Select-all applies to every option, not just the ones currently matching the search. */
  onToggleAll(checked: boolean): void {
    this.selectedHashtags = checked ? [...this.allHashtags] : [];
    this.selectionChange.emit([...this.selectedHashtags]);
  }

  clearSelection(event?: Event): void {
    event?.stopPropagation();
    this.onToggleAll(false);
  }

  private applySearch(): void {
    const needle = hashtagKey(this.searchControl.value ?? '');
    this.filteredHashtags = needle
      ? this.allHashtags.filter((tag) => hashtagKey(tag).includes(needle))
      : [...this.allHashtags];
  }

  private dedupePreservingStoredValue(tags: readonly (string | null | undefined)[]): string[] {
    const seen = new Set<string>();

    return tags.filter((tag): tag is string => {
      const key = hashtagKey(tag);
      if (!key || seen.has(key)) {
        return false;
      }

      seen.add(key);
      return true;
    });
  }
}
